// Terms and Conditions service for fetching legal content

import { apiUtils } from '../hooks/useApi';
import { API_ENDPOINTS } from './apiEndpoints';

// TypeScript interfaces for terms data
export interface TermsData {
  document: {
    id: string;
    title: string;
    version: string;
    effective_date: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  id: number;
  content: string;
  content_html: string;
}

export interface TermsApiResponse {
  success: boolean;
  data: TermsData;
}

/**
 * Terms Service class for handling terms and conditions API operations
 */
export class TermsService {
  /**
   * Fetch terms and conditions data from API
   * @returns Promise<TermsApiResponse>
   */
  static async fetchTermsData(): Promise<TermsApiResponse> {
    try {
      const response = await apiUtils.get(API_ENDPOINTS.staticPages.terms);

      // Assuming the response matches the structure in termsData.json
      // which has { success: true, data: {...} }
      return response.data as unknown as TermsApiResponse;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch terms and conditions';

      throw new Error(errorMessage);
    }
  }

  /**
   * Check if terms data is valid and active
   * @param termsData - The terms data to validate
   * @returns boolean
   */
  static isValidTermsData(termsData: TermsData | null): boolean {
    return !!(
      termsData &&
      termsData.document.is_active &&
      termsData.content &&
      termsData.document.title
    );
  }

  /**
   * Format effective date for display
   * @param dateString - ISO date string
   * @returns Formatted date string
   */
  static formatEffectiveDate(dateString: string): string {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }
}
